// Sidebar "Flags" list: every flagged-for-follow-up email in the Inbox
// (GET /api/mail/flagged).
//
// The server list lags an in-app flag change: it is cached for 30 s and a
// refresh can be in flight while the write lands. So a flag / unflag / tick
// made in this tab is recorded as an EDIT that overrides the server's answer —
// the row appears or drops off the moment you click.
//
// An edit is released by the SERVER, never by a clock: only a refresh that
// started after the click, and agrees with it, lets it go (settleEdits). It
// used to lapse after 90 s, and the list fell back to rows fetched BEFORE the
// click: taylor@ ticked "Lanyards PO 3213A" done, a poke from his previous
// click had just fetched it still flagged, and 90 s later it was back in the
// list until the next poll — 5 minutes with SSE on (2026-10-29). Each edit
// also asks for a fresh list a moment later, so the server's answer arrives
// without waiting for a poll.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// How long the server may DISAGREE with an edit (on a refresh that started after
// it) before the server wins — the email was changed again elsewhere (Outlook,
// another tab), or the write failed without telling us.
export const FLAG_EDIT_HOLD_MS = 90_000;
// The fresh list fetched after an edit: long enough for the flag write (~0.4–1.4 s)
// to land and clear the server's cache.
export const REFRESH_AFTER_EDIT_MS = 3_000;
// Floor between non-forced refreshes: the folders poll, focus, visibility and
// an SSE poke can all ask within the same second.
const MIN_REFRESH_GAP_MS = 15_000;

const newestFirst = (a, b) => String(b.date || '').localeCompare(String(a.date || ''));

// Server rows overlaid with this tab's edits, newest first.
// edits: Map id → { flagged, completed, row, at }.
export function mergeFlagged(serverRows, edits) {
  const byId = new Map();
  for (const m of serverRows || []) if (m?.id) byId.set(m.id, m);
  for (const [id, e] of edits || []) {
    if (e.flagged && !e.completed) {
      const base = byId.get(id) || e.row;
      if (base) byId.set(id, { ...base, flagged: true, completed: false });
    } else {
      byId.delete(id);
    }
  }
  return [...byId.values()].sort(newestFirst);
}

// The edits a refresh that started at `startedAt` and returned `rows` lets go:
// it began after the edit, and either lists the email exactly as the edit says,
// or has disagreed for FLAG_EDIT_HOLD_MS. A refresh that started before an edit
// says nothing about it. Returns the same Map when nothing is released.
export function settleEdits(edits, rows, startedAt, now = Date.now()) {
  const listed = new Set((rows || []).map(m => m && m.id));
  let next = edits;
  for (const [id, e] of edits) {
    if (startedAt <= e.at) continue;
    const agrees = listed.has(id) === (e.flagged && !e.completed);
    if (!agrees && now - e.at < FLAG_EDIT_HOLD_MS) continue;
    if (next === edits) next = new Map(edits);
    next.delete(id);
  }
  return next;
}

export default function useFlaggedMail() {
  const [rows, setRows]     = useState([]);
  const [more, setMore]     = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [edits, setEdits]   = useState(() => new Map());
  const lastAtRef   = useRef(0);
  const inFlightRef = useRef(null);
  const afterEditTimer = useRef(null);

  const refresh = useCallback(({ force = false, bg = true } = {}) => {
    if (inFlightRef.current) {
      // A forced refresh wants an answer from AFTER now; the one in flight may
      // have started before the edit that asked for it.
      return force ? inFlightRef.current.then(() => refresh({ force, bg })) : inFlightRef.current;
    }
    const startedAt = Date.now();
    if (!force && startedAt - lastAtRef.current < MIN_REFRESH_GAP_MS) return Promise.resolve();
    lastAtRef.current = startedAt;
    const p = fetch(`/api/mail/flagged${bg ? '?bg=1' : ''}`, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => {
        const fresh = Array.isArray(d?.messages) ? d.messages : [];
        setRows(fresh);
        setMore(!!d?.more);
        setEdits(prev => settleEdits(prev, fresh, startedAt));
      })
      .catch(() => { /* keep the last good list and every edit; the next poll tries again */ })
      .finally(() => {
        inFlightRef.current = null;
        setLoaded(true);
      });
    inFlightRef.current = p;
    return p;
  }, []);

  useEffect(() => { refresh({ force: true, bg: false }); }, [refresh]);
  useEffect(() => () => clearTimeout(afterEditTimer.current), []);

  // Record an in-app flag change. `row` is the message as the caller knows it,
  // used to show a newly flagged email before the server list has it.
  const noteFlag = useCallback((id, { flagged, completed }, row) => {
    if (!id) return;
    setEdits(prev => new Map(prev).set(id, {
      flagged: !!flagged, completed: !!completed, row: row || null, at: Date.now(),
    }));
    clearTimeout(afterEditTimer.current);
    afterEditTimer.current = setTimeout(() => refresh({ force: true }), REFRESH_AFTER_EDIT_MS);
  }, [refresh]);

  const list = useMemo(() => mergeFlagged(rows, edits), [rows, edits]);

  return { list, more, loaded, refresh, noteFlag };
}
